import React, { useCallback, useEffect, useState } from 'react'
import Table from '../../components/Table'
import FeedbackThreadModal from '../../components/FeedbackThreadModal'
import AppStoreReviewModal from '../../components/AppStoreReviewModal'
import { CommentDto, AppStoreReviewDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'

type FeedbackRow = CommentDto & { statusLabel: string; typeLabel: string }
type AppStoreReviewRow = AppStoreReviewDto & { statusLabel: string; ratingLabel: string }

const renderStars = (rating: number) => '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating))

type FeedbackTab = 'mobile' | 'apple'

const CommentsPage = () => {
  const [activeTab, setActiveTab] = useState<FeedbackTab>('mobile')

  const [comments, setComments] = useState<FeedbackRow[] | undefined>(undefined)
  const [selectedPublicId, setSelectedPublicId] = useState<string | null>(null)

  const [appStoreReviews, setAppStoreReviews] = useState<AppStoreReviewRow[] | undefined>(undefined)
  const [selectedReviewPublicId, setSelectedReviewPublicId] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)

  const fetchComments = useCallback(async () => {
    try {
      const response = await adminService.getFeedbackTopics()
      setComments(
        response.map(comment => ({
          ...comment,
          statusLabel: comment.closedAt ? 'Closed' : 'Open',
          typeLabel: comment.venueSuggestion ? 'Venue suggestion' : comment.refundRequest ? 'Refund request' : 'General',
        }))
      )
    } catch (err) {
      console.log('Failed to fetch comments', err)
    }
  }, [])

  const fetchAppStoreReviews = useCallback(async () => {
    try {
      const response = await adminService.getAppStoreReviews()
      setAppStoreReviews(
        response.map(review => ({
          ...review,
          statusLabel: review.isResponded ? 'Responded' : 'Awaiting reply',
          ratingLabel: renderStars(review.rating),
        }))
      )
    } catch (err) {
      console.log('Failed to fetch App Store reviews', err)
    }
  }, [])

  useEffect(() => {
    fetchComments()
    fetchAppStoreReviews()
  }, [fetchComments, fetchAppStoreReviews])

  const handleSyncNow = async () => {
    if (isSyncing) return
    setIsSyncing(true)
    try {
      await adminService.syncAppStoreReviews()
      await fetchAppStoreReviews()
    } catch (err) {
      console.log('Failed to sync App Store reviews', err)
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <div>
      <div className="comments-container">
        <h2>Feedback</h2>

        <div className="tab-bar">
          <div style={{ display: 'flex', gap: '24px' }}>
            <button
              type="button"
              className={`tab ${activeTab === 'mobile' ? 'active' : ''}`}
              onClick={() => setActiveTab('mobile')}
            >
              Mobile
            </button>
            <button
              type="button"
              className={`tab ${activeTab === 'apple' ? 'active' : ''}`}
              onClick={() => setActiveTab('apple')}
            >
              Apple
            </button>
          </div>
        </div>

        {activeTab === 'mobile' ? (
          <Table
            id="comments"
            name="Feedback"
            keyField="publicId"
            initialSortColumn="lastReplyAt"
            initialSortOrder="desc"
            columns={[
              { label: '', accessor: 'account.imageUrl', type: 'userAvatar' },
              { label: 'Name/Email', accessor: 'account.lastName', type: 'fullNameAndEmail' },
              { label: 'Type', accessor: 'typeLabel', type: 'text' },
              { label: 'Subject', accessor: 'subject', type: 'text' },
              { label: 'Latest', accessor: 'lastReplyPreview', type: 'text' },
              { label: 'Status', accessor: 'statusLabel', type: 'text' },
              { label: 'Updated', accessor: 'lastReplyAt', type: 'datetime' },
            ]}
            sourceData={comments || []}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={(row: FeedbackRow) => setSelectedPublicId(row.publicId)}
            options={[
              {
                label: 'Close topic',
                onClick: async (row: FeedbackRow) => {
                  if (row.closedAt) return
                  await adminService.closeFeedbackTopic(row.publicId)
                  fetchComments()
                },
              },
            ]}
          />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <button type="button" className="styled-button" onClick={handleSyncNow} disabled={isSyncing}>
                {isSyncing ? 'Syncing…' : 'Sync now'}
              </button>
            </div>
            <Table
              id="app-store-reviews"
              name="App Store Reviews"
              keyField="publicId"
              initialSortColumn="appleCreatedAt"
              initialSortOrder="desc"
              columns={[
                { label: 'Rating', accessor: 'ratingLabel', type: 'text' },
                { label: 'Reviewer', accessor: 'reviewerNickname', type: 'text' },
                { label: 'Title', accessor: 'title', type: 'text' },
                { label: 'Territory', accessor: 'territory', type: 'text' },
                { label: 'Status', accessor: 'statusLabel', type: 'text' },
                { label: 'Posted', accessor: 'appleCreatedAt', type: 'datetime' },
              ]}
              sourceData={appStoreReviews || []}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AppStoreReviewRow) => setSelectedReviewPublicId(row.publicId)}
            />
          </>
        )}
      </div>
      <FeedbackThreadModal
        commentPublicId={selectedPublicId}
        isOpen={!!selectedPublicId}
        onClose={() => setSelectedPublicId(null)}
        onChanged={fetchComments}
      />
      <AppStoreReviewModal
        reviewPublicId={selectedReviewPublicId}
        isOpen={!!selectedReviewPublicId}
        onClose={() => setSelectedReviewPublicId(null)}
        onChanged={fetchAppStoreReviews}
      />
    </div>
  )
}

export default CommentsPage
